<template>
  <div class="bulk-price-update">
    <div class="bpu-header">
      <div class="bpu-header__icon">
        <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8">
          <path d="M12 2L2 7l10 5 10-5-10-5z"/>
          <path d="M2 17l10 5 10-5"/>
          <path d="M2 12l10 5 10-5"/>
        </svg>
      </div>
      <div>
        <h2 class="bpu-header__title">Actualización Masiva de Precios</h2>
        <p class="bpu-header__subtitle">Modifica el Gasto Inmueble de múltiples alojamientos a la vez</p>
      </div>
    </div>

    <div class="bpu-grid">
      <!-- Columna Izquierda: IDs -->
      <div class="bpu-card">
        <div class="bpu-card__label">
          <span class="bpu-card__badge bpu-card__badge--ids">IDs</span>
          <span class="bpu-card__hint">Un ID por línea</span>
        </div>
        <textarea
          v-model="rawIds"
          class="bpu-textarea"
          placeholder="Ej:&#10;1990&#10;1234&#10;567"
          spellcheck="false"
          @input="clearResults"
        />
        <div class="bpu-card__count">{{ idList.length }} alojamiento{{ idList.length !== 1 ? 's' : '' }}</div>
      </div>

      <!-- Columna Derecha: Precios -->
      <div class="bpu-card">
        <div class="bpu-card__label">
          <span class="bpu-card__badge bpu-card__badge--prices">Precios</span>
          <span class="bpu-card__hint">Un precio por línea (arriendo neto)</span>
        </div>
        <textarea
          v-model="rawPrices"
          class="bpu-textarea"
          placeholder="Ej:&#10;345&#10;670&#10;789"
          spellcheck="false"
          @input="clearResults"
        />
        <div class="bpu-card__count">{{ priceList.length }} precio{{ priceList.length !== 1 ? 's' : '' }}</div>
      </div>
    </div>

    <!-- Validación de conteo -->
    <transition name="fade">
      <div v-if="rawIds && rawPrices && idList.length !== priceList.length" class="bpu-alert bpu-alert--warning">
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z"/>
          <line x1="12" y1="9" x2="12" y2="13"/>
          <line x1="12" y1="17" x2="12.01" y2="17"/>
        </svg>
        La cantidad de IDs ({{ idList.length }}) no coincide con la de precios ({{ priceList.length }})
      </div>
    </transition>

    <!-- Botón Preview -->
    <div class="bpu-actions">
      <button
        class="bpu-btn bpu-btn--preview"
        :disabled="!canPreview || loadingPreview"
        @click="fetchPreview"
      >
        <span v-if="loadingPreview" class="bpu-spinner" />
        <svg v-else width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
          <circle cx="12" cy="12" r="3"/>
        </svg>
        {{ loadingPreview ? 'Consultando...' : 'Vista Previa' }}
      </button>
    </div>

    <!-- Tabla de Preview -->
    <transition name="slide-up">
      <div v-if="previewItems.length > 0" class="bpu-preview">
        <div class="bpu-preview__header">
          <h3 class="bpu-preview__title">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"/>
              <polyline points="14 2 14 8 20 8"/>
            </svg>
            Vista Previa de Cambios
          </h3>
          <span class="bpu-preview__count">{{ previewItems.length }} registro{{ previewItems.length !== 1 ? 's' : '' }}</span>
        </div>

        <div class="bpu-table-wrapper">
          <table class="bpu-table">
            <thead>
              <tr>
                <th>ID</th>
                <th>Nombre Alojamiento</th>
                <th>Arriendo Actual</th>
                <th>Nuevo Arriendo</th>
                <th>Básicos</th>
                <th>Comunes</th>
                <th>Limpieza</th>
                <th>Nuevo Total</th>
                <th>Estado</th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="item in previewItems"
                :key="item.id"
                :class="{ 'bpu-table__row--error': item.error }"
              >
                <td class="bpu-table__id">{{ item.id }}</td>
                <td class="bpu-table__name">
                  <span v-if="item.error" class="bpu-text--muted">—</span>
                  <span v-else>{{ item.nombre || '—' }}</span>
                </td>
                <td>
                  <span v-if="item.error" class="bpu-text--muted">—</span>
                  <span v-else class="bpu-price bpu-price--old">${{ formatNumber(item.arriendo_actual) }}</span>
                </td>
                <td>
                  <span v-if="item.error" class="bpu-text--muted">—</span>
                  <span v-else class="bpu-price bpu-price--new">${{ formatNumber(item.nuevo_arriendo) }}</span>
                </td>
                <td>
                  <span v-if="item.error" class="bpu-text--muted">—</span>
                  <span v-else class="bpu-text--muted">${{ formatNumber(item.arriendo_basicos) }}</span>
                </td>
                <td>
                  <span v-if="item.error" class="bpu-text--muted">—</span>
                  <span v-else class="bpu-text--muted">${{ formatNumber(item.arriendo_comunes) }}</span>
                </td>
                <td>
                  <span v-if="item.error" class="bpu-text--muted">—</span>
                  <span v-else class="bpu-text--muted">${{ formatNumber(item.arriendo_limpieza) }}</span>
                </td>
                <td>
                  <span v-if="item.error" class="bpu-text--muted">—</span>
                  <span v-else class="bpu-price bpu-price--total">${{ formatNumber(item.nuevo_total) }}</span>
                </td>
                <td>
                  <span v-if="item.error" class="bpu-badge bpu-badge--error" :title="item.error">
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
                    No encontrado
                  </span>
                  <span v-else class="bpu-badge bpu-badge--ok">
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><polyline points="20 6 9 17 4 12"/></svg>
                    Listo
                  </span>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <!-- Resumen -->
        <div class="bpu-summary">
          <div class="bpu-summary__item">
            <span class="bpu-summary__label">Válidos</span>
            <span class="bpu-summary__value bpu-summary__value--ok">{{ validCount }}</span>
          </div>
          <div class="bpu-summary__item">
            <span class="bpu-summary__label">Con error</span>
            <span class="bpu-summary__value bpu-summary__value--error">{{ errorCount }}</span>
          </div>
          <div class="bpu-summary__sep" />
          <button
            class="bpu-btn bpu-btn--apply"
            :disabled="validCount === 0 || loadingApply || applied"
            @click="applyChanges"
          >
            <span v-if="loadingApply" class="bpu-spinner bpu-spinner--white" />
            <svg v-else-if="applied" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><polyline points="20 6 9 17 4 12"/></svg>
            <svg v-else width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M19 21H5a2 2 0 01-2-2V5a2 2 0 012-2h11l5 5v14a2 2 0 01-2 2z"/><polyline points="17 21 17 13 7 13 7 21"/><polyline points="7 3 7 8 15 8"/></svg>
            {{ loadingApply ? 'Aplicando...' : applied ? '¡Cambios aplicados!' : `Aplicar ${validCount} cambio${validCount !== 1 ? 's' : ''}` }}
          </button>
        </div>
      </div>
    </transition>

    <!-- Resultados de aplicación -->
    <transition name="fade">
      <div v-if="applyResults.length > 0" class="bpu-results">
        <h3 class="bpu-results__title">Resultado de la operación</h3>
        <div
          v-for="res in applyResults"
          :key="res.id"
          class="bpu-result-row"
          :class="res.success ? 'bpu-result-row--ok' : 'bpu-result-row--error'"
        >
          <svg v-if="res.success" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><polyline points="20 6 9 17 4 12"/></svg>
          <svg v-else width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
          <span><strong>ID {{ res.id }}</strong> — {{ res.message }}</span>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
import axios from 'axios'
import apiUrls from '../../../new-constants/apiUrls'

export default {
  name: 'BulkPriceUpdate',

  data() {
    return {
      rawIds: '',
      rawPrices: '',
      loadingPreview: false,
      loadingApply: false,
      previewItems: [],
      applyResults: [],
      applied: false,
    }
  },

  computed: {
    idList() {
      return this.rawIds
        .split('\n')
        .map(s => s.trim())
        .filter(s => s !== '' && !isNaN(s))
        .map(Number)
    },
    priceList() {
      return this.rawPrices
        .split('\n')
        .map(s => s.trim())
        .filter(s => s !== '' && !isNaN(s))
        .map(Number)
    },
    canPreview() {
      return (
        this.idList.length > 0 &&
        this.priceList.length > 0 &&
        this.idList.length === this.priceList.length
      )
    },
    validCount() {
      return this.previewItems.filter(i => !i.error).length
    },
    errorCount() {
      return this.previewItems.filter(i => i.error).length
    },
  },

  methods: {
    clearResults() {
      this.previewItems = []
      this.applyResults = []
      this.applied = false
    },

    formatNumber(val) {
      if (val == null || val === '') return '0'
      return Number(val).toLocaleString('es-CL')
    },

    async fetchPreview() {
      this.loadingPreview = true
      this.previewItems = []
      this.applyResults = []
      this.applied = false

      try {
        const payload = { items: this.idList.map((id, i) => ({ id, nuevo_arriendo: this.priceList[i] })) }
        const { data } = await axios.post(apiUrls.bulkPricePreview, payload)
        // Espera: { items: [ { id, nombre, arriendo_actual, nuevo_arriendo, arriendo_basicos, arriendo_comunes, arriendo_limpieza, nuevo_total, error? } ] }
        this.previewItems = data.items || []
      } catch (err) {
        if (err.response && err.response.status === 422) {
          const msgs = err.response.data && err.response.data.errors ? Object.values(err.response.data.errors).flat().join('; ') : JSON.stringify(err.response.data)
          this.$emit('error', `Errores de validación: ${msgs}`)
        } else {
          console.error('Error en preview:', err)
          this.$emit('error', 'No se pudo obtener la vista previa. Revisa la conexión con el servidor.')
        }
      } finally {
        this.loadingPreview = false
      }
    },

    async applyChanges() {
      if (this.validCount === 0) return
      this.loadingApply = true

      const validItems = this.previewItems
        .filter(i => !i.error)
        .map(i => ({
          id: i.id,
          nuevo_arriendo: i.nuevo_arriendo,
        }))

      try {
        const { data } = await axios.post(apiUrls.bulkPriceUpdate, { items: validItems })
        // Espera: { results: [ { id, success, message, accommodation? } ] }
        this.applyResults = data.results || []
        this.applied = true
        this.$emit('prices-updated', data.results)
      } catch (err) {
        if (err.response && err.response.status === 422) {
          const msgs = err.response.data && err.response.data.errors ? Object.values(err.response.data.errors).flat().join('; ') : JSON.stringify(err.response.data)
          this.$emit('error', `Errores de validación: ${msgs}`)
          return
        }
        console.error('Error al aplicar cambios:', err)
        this.$emit('error', 'Ocurrió un error al guardar los cambios.')
      } finally {
        this.loadingApply = false
      }
    },
  },
}
</script>

<style scoped>
/* ── Variables ── */
.bulk-price-update {
  --c-bg: #0f1117;
  --c-surface: #181c27;
  --c-surface2: #1e2336;
  --c-border: #2a3050;
  --c-accent: #4f8ef7;
  --c-accent-dim: rgba(79, 142, 247, 0.12);
  --c-green: #3ecf8e;
  --c-green-dim: rgba(62, 207, 142, 0.12);
  --c-red: #f76f6f;
  --c-red-dim: rgba(247, 111, 111, 0.1);
  --c-yellow: #f7c04f;
  --c-yellow-dim: rgba(247, 192, 79, 0.1);
  --c-text: #e4e9f7;
  --c-muted: #6b7599;
  --radius: 10px;
  --font: 'DM Sans', 'Segoe UI', sans-serif;

  font-family: var(--font);
  background: var(--c-bg);
  color: var(--c-text);
  padding: 2rem;
  min-height: 100vh;
  max-width: 1100px;
  margin: 0 auto;
}

/* ── Header ── */
.bpu-header {
  display: flex;
  align-items: center;
  gap: 1rem;
  margin-bottom: 2rem;
  padding-bottom: 1.5rem;
  border-bottom: 1px solid var(--c-border);
}
.bpu-header__icon {
  width: 52px;
  height: 52px;
  border-radius: 12px;
  background: var(--c-accent-dim);
  border: 1px solid rgba(79,142,247,0.25);
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--c-accent);
  flex-shrink: 0;
}
.bpu-header__title {
  font-size: 1.35rem;
  font-weight: 700;
  margin: 0 0 0.2rem;
  letter-spacing: -0.02em;
}
.bpu-header__subtitle {
  font-size: 0.82rem;
  color: var(--c-muted);
  margin: 0;
}

/* ── Grid de inputs ── */
.bpu-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem;
  margin-bottom: 1rem;
}
@media (max-width: 640px) {
  .bpu-grid { grid-template-columns: 1fr; }
}

/* ── Card ── */
.bpu-card {
  background: var(--c-surface);
  border: 1px solid var(--c-border);
  border-radius: var(--radius);
  padding: 1rem;
  display: flex;
  flex-direction: column;
  gap: 0.6rem;
}
.bpu-card__label {
  display: flex;
  align-items: center;
  gap: 0.6rem;
}
.bpu-card__badge {
  font-size: 0.7rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  padding: 0.22rem 0.6rem;
  border-radius: 20px;
}
.bpu-card__badge--ids {
  background: var(--c-accent-dim);
  color: var(--c-accent);
  border: 1px solid rgba(79,142,247,0.3);
}
.bpu-card__badge--prices {
  background: var(--c-green-dim);
  color: var(--c-green);
  border: 1px solid rgba(62,207,142,0.3);
}
.bpu-card__hint {
  font-size: 0.75rem;
  color: var(--c-muted);
}
.bpu-card__count {
  font-size: 0.72rem;
  color: var(--c-muted);
  text-align: right;
}

/* ── Textarea ── */
.bpu-textarea {
  background: var(--c-bg);
  border: 1px solid var(--c-border);
  border-radius: 6px;
  color: var(--c-text);
  font-family: 'JetBrains Mono', 'Fira Code', monospace;
  font-size: 0.85rem;
  line-height: 1.7;
  padding: 0.75rem;
  resize: vertical;
  min-height: 160px;
  outline: none;
  transition: border-color 0.2s;
  width: 100%;
  box-sizing: border-box;
}
.bpu-textarea:focus {
  border-color: var(--c-accent);
  box-shadow: 0 0 0 3px rgba(79,142,247,0.1);
}
.bpu-textarea::placeholder { color: var(--c-muted); opacity: 0.6; }

/* ── Alert ── */
.bpu-alert {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.7rem 1rem;
  border-radius: var(--radius);
  font-size: 0.82rem;
  margin-bottom: 0.5rem;
}
.bpu-alert--warning {
  background: var(--c-yellow-dim);
  border: 1px solid rgba(247,192,79,0.3);
  color: var(--c-yellow);
}

/* ── Actions ── */
.bpu-actions {
  display: flex;
  justify-content: flex-end;
  margin-bottom: 1.5rem;
}

/* ── Buttons ── */
.bpu-btn {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.65rem 1.4rem;
  border-radius: 8px;
  font-family: var(--font);
  font-size: 0.85rem;
  font-weight: 600;
  border: none;
  cursor: pointer;
  transition: all 0.18s;
}
.bpu-btn:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}
.bpu-btn--preview {
  background: var(--c-accent-dim);
  color: var(--c-accent);
  border: 1px solid rgba(79,142,247,0.3);
}
.bpu-btn--preview:not(:disabled):hover {
  background: rgba(79,142,247,0.22);
  transform: translateY(-1px);
}
.bpu-btn--apply {
  background: var(--c-green);
  color: #0f1117;
}
.bpu-btn--apply:not(:disabled):hover {
  filter: brightness(1.1);
  transform: translateY(-1px);
}

/* ── Preview section ── */
.bpu-preview {
  background: var(--c-surface);
  border: 1px solid var(--c-border);
  border-radius: var(--radius);
  overflow: hidden;
  margin-bottom: 1.5rem;
}
.bpu-preview__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1rem 1.2rem;
  border-bottom: 1px solid var(--c-border);
}
.bpu-preview__title {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.9rem;
  font-weight: 600;
  margin: 0;
  color: var(--c-text);
}
.bpu-preview__count {
  font-size: 0.75rem;
  color: var(--c-muted);
}

/* ── Table ── */
.bpu-table-wrapper {
  overflow-x: auto;
}
.bpu-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 0.82rem;
}
.bpu-table th {
  padding: 0.65rem 1rem;
  text-align: left;
  font-size: 0.7rem;
  text-transform: uppercase;
  letter-spacing: 0.07em;
  color: var(--c-muted);
  background: var(--c-surface2);
  border-bottom: 1px solid var(--c-border);
  white-space: nowrap;
}
.bpu-table td {
  padding: 0.75rem 1rem;
  border-bottom: 1px solid rgba(42,48,80,0.5);
  vertical-align: middle;
}
.bpu-table tr:last-child td { border-bottom: none; }
.bpu-table tr:hover td { background: rgba(255,255,255,0.02); }
.bpu-table__row--error td { opacity: 0.6; }
.bpu-table__id { font-family: monospace; font-weight: 600; color: var(--c-accent); }
.bpu-table__name { max-width: 160px; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }

/* ── Prices ── */
.bpu-price { font-family: monospace; font-weight: 600; }
.bpu-price--old { color: var(--c-muted); }
.bpu-price--new { color: var(--c-accent); }
.bpu-price--total { color: var(--c-green); }
.bpu-text--muted { color: var(--c-muted); }

/* ── Badges de estado ── */
.bpu-badge {
  display: inline-flex;
  align-items: center;
  gap: 0.3rem;
  font-size: 0.7rem;
  font-weight: 600;
  padding: 0.22rem 0.55rem;
  border-radius: 20px;
  white-space: nowrap;
}
.bpu-badge--ok {
  background: var(--c-green-dim);
  color: var(--c-green);
  border: 1px solid rgba(62,207,142,0.3);
}
.bpu-badge--error {
  background: var(--c-red-dim);
  color: var(--c-red);
  border: 1px solid rgba(247,111,111,0.3);
  cursor: help;
}

/* ── Summary ── */
.bpu-summary {
  display: flex;
  align-items: center;
  gap: 1.5rem;
  padding: 1rem 1.2rem;
  border-top: 1px solid var(--c-border);
  background: var(--c-surface2);
}
.bpu-summary__item {
  display: flex;
  flex-direction: column;
  gap: 0.15rem;
}
.bpu-summary__label { font-size: 0.68rem; color: var(--c-muted); text-transform: uppercase; letter-spacing: 0.06em; }
.bpu-summary__value { font-size: 1.1rem; font-weight: 700; }
.bpu-summary__value--ok { color: var(--c-green); }
.bpu-summary__value--error { color: var(--c-red); }
.bpu-summary__sep { flex: 1; }

/* ── Results ── */
.bpu-results {
  background: var(--c-surface);
  border: 1px solid var(--c-border);
  border-radius: var(--radius);
  padding: 1.2rem;
}
.bpu-results__title {
  font-size: 0.85rem;
  font-weight: 600;
  margin: 0 0 0.8rem;
  color: var(--c-muted);
  text-transform: uppercase;
  letter-spacing: 0.06em;
}
.bpu-result-row {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.4rem 0;
  font-size: 0.82rem;
  border-bottom: 1px solid rgba(42,48,80,0.4);
}
.bpu-result-row:last-child { border-bottom: none; }
.bpu-result-row--ok { color: var(--c-green); }
.bpu-result-row--error { color: var(--c-red); }

/* ── Spinner ── */
.bpu-spinner {
  width: 14px;
  height: 14px;
  border: 2px solid currentColor;
  border-top-color: transparent;
  border-radius: 50%;
  display: inline-block;
  animation: spin 0.7s linear infinite;
}
@keyframes spin { to { transform: rotate(360deg); } }

/* ── Transitions ── */
.fade-enter-active, .fade-leave-active { transition: opacity 0.25s; }
.fade-enter, .fade-leave-to { opacity: 0; }
.slide-up-enter-active { transition: all 0.3s ease; }
.slide-up-enter { opacity: 0; transform: translateY(12px); }
</style>